import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import _ from 'lodash';
import moment from 'moment';
import { toast } from 'react-toastify';
import DatePickerComponent from '../common/DatePickerComponent';
import LoaderSpinner from '../common/Loader';
import { fetchCompanyFilters, getFilterSellers } from '../../store/actions/rateCard';
import { getAllSellersList, getAllPricingServiceTypes, getPricingSubServiceTypesById, getPricingSkusById, getPricingCarrierConnectIds } from '../../store/actions/company';
import { getWarehouseListing } from '../../store/actions/inventory.action';
import { getSellerWarehouseService } from '../../services/company.service';
import { getSizeTypeList, getChargeUnitTypeList, getPackingTypeList, getTransactionTypeList } from '../../store/actions/serviceRateCard.action';
import { createUpdateCustomPricingTemplate, fetchCustomPricingTemplateById } from '../../store/actions/customPricingTemplate.action';
import { getSelectList, computeAutoSelectedSellers } from '../../config/utils';


const getServiceTypeValuesFromTemplate = (data) => {
    if (data.serviceTypeIds?.length) {
        return data.serviceTypeIds;
    }

    const serviceTypes = data.serviceTypes || [];
    if (serviceTypes.length) {
        return _.map(serviceTypes, (item) => (typeof item === 'object' ? item.value : item));
    }

    if (data.serviceTypeMasterId) {
        return [data.serviceTypeMasterId];
    }

    return [];
};

const getSelectedOptionsByIds = (options = [], ids = [], idSelector = (item) => item.value) => {
    if (!ids?.length) return [];
    const idSet = new Set(ids);
    return options.filter((item) => idSet.has(idSelector(item)));
};

const filterOptionsByValueSet = (options = [], idSet = new Set()) => {
    return options.filter((item) => idSet.has(item.value));
};

const normalizeIdList = (list = []) => {
    return _.chain(list)
        .map((item) => Number.parseInt(item, 10))
        .filter((item) => Number.isFinite(item) && item > 0)
        .uniq()
        .sortBy()
        .value();
};

export const buildWarehouseRequestPairs = (companyIds = [], sellerIds = []) => {
    const normalizedCompanyIds = normalizeIdList(companyIds);
    const normalizedSellerIds = normalizeIdList(sellerIds);

    return normalizedCompanyIds.flatMap((companyId) => (
        normalizedSellerIds.map((sellerId) => ({ companyId, sellerId }))
    ));
};

export const mapSellerWarehouseEntityToOptions = (entityList = []) => {
    return (entityList || [])
        .map((item) => {
            const warehouseId = Number.parseInt(
                item?.Warehouse_Id ?? item?.warehouse_Id ?? item?.warehouseId ?? item?.id,
                10
            );
            const warehouseName = item?.WarehouseName || item?.warehouseName || item?.location || String(warehouseId || '');

            return {
                id: warehouseId,
                value: warehouseId,
                label: warehouseName,
                ...item,
            };
        })
        .filter((item) => Number.isFinite(item.value) && item.value > 0);
};

const mapCarrierConnectDetailsToOptions = (details = []) => {
    return (details || []).map((item) => {
        const id = Number.parseInt(item?.id, 10);
        const carrierConnectNumber = item?.carrierConnectNumber || String(id || '');
        const companyName = item?.companyName || '';
        const sellerName = item?.sellerName || '';
        const contextLabel = [companyName, sellerName].filter(Boolean).join(' / ');

        return {
            value: id,
            label: contextLabel
                ? `${carrierConnectNumber} - ${contextLabel}`
                : carrierConnectNumber,
            carrierConnectNumber,
            companySellerId: item?.companySellerId || 0,
            companyId: item?.companyId || 0,
            companyName,
            sellerId: item?.sellerId || 0,
            sellerName,
        };
    }).filter((item) => Number.isFinite(item.value) && item.value > 0);
};

const getRequestCacheKey = (serviceTypeId, companyIds, sellerIds, warehouseIds, searchText = '') => {
    return JSON.stringify({
        serviceTypeId,
        companyIds,
        sellerIds,
        warehouseIds,
        searchText,
    });
};

const getSimpleRequestCacheKey = (serviceTypeId, companyIds, sellerIds, searchText = '') => {
    return JSON.stringify({
        serviceTypeId,
        companyIds,
        sellerIds,
        searchText,
    });
};

let uidCounter = 0;
const genUid = () => `rate_${++uidCounter}_${Date.now()}`;

const tableSelectStyles = {
    control: (provided) => ({
        ...provided,
        minHeight: '36px',
        fontSize: '13px',
        borderRadius: '4px',
        border: '1px solid #ced4da',
        boxShadow: 'none',
        '&:hover': { borderColor: '#007bff' }
    }),
    valueContainer: (provided) => ({
        ...provided,
        padding: '0 8px'
    }),
    input: (provided) => ({
        ...provided,
        margin: '0',
        padding: '0'
    }),
    indicatorsContainer: (provided) => ({
        ...provided,
        height: '34px'
    }),
    menu: (provided) => ({
        ...provided,
        zIndex: 9999,
        fontSize: '13px'
    }),
    menuList: (provided) => ({
        ...provided,
        maxHeight: '200px',
        padding: '2px 0'
    }),
    option: (provided, state) => {
        let bg = 'white';
        if (state.isSelected) bg = '#007bff';
        else if (state.isFocused) bg = '#f8f9fa';
        return {
            ...provided,
            padding: '6px 12px',
            fontSize: '13px',
            cursor: 'pointer',
            backgroundColor: bg,
            color: state.isSelected ? 'white' : '#495057',
            '&:hover': { backgroundColor: state.isSelected ? '#007bff' : '#f8f9fa' }
        };
    },
    menuPortal: (provided) => ({
        ...provided,
        zIndex: 9999
    })
};

const formatSkuOptionLabel = (option, { context }) => {
    if (context === 'value') {
        return option.sku || option.label;
    }

    return (
        <div className="d-flex flex-column">
            <span className="font-weight-bold">{option.sku || option.label}</span>
            <small>{option.itemName}</small>
            <small className="text-muted">{[option.companyName, option.sellerName].filter(Boolean).join(' | ')}</small>
        </div>
    );
};

const emptyRateRow = () => ({
    _uid: genUid(),
    transactionType: '',
    chargeType: '',
    size: '',
    chargeUnit: '',
    validFrom: null,
    validTo: null,
    amount: '',
    additionalCharge: '',
});

const getRateRowSignature = (row) => [
    row.transactionType?.value ?? row.transactionType ?? '',
    row.chargeType?.value ?? row.chargeType ?? '',
    row.size?.value ?? row.size ?? '',
    row.chargeUnit?.value ?? row.chargeUnit ?? '',
].join('|');

export const getRateRowDateError = (row) => {
    if (!row.validFrom) return 'Valid From is required';
    if (row.validTo && new Date(row.validFrom) > new Date(row.validTo)) return 'Valid From must be earlier than or equal to Valid To';
    if (new Date(row.validFrom) > new Date()) return 'Valid From cannot be a future date';
    return '';
};

const validateRateRow = (row) => {
    const rowErr = {};
    if (!row.transactionType) rowErr.transactionType = 'Required';
    if (!row.chargeType) rowErr.chargeType = 'Required';
    if (!row.size) rowErr.size = 'Required';
    if (!row.chargeUnit) rowErr.chargeUnit = 'Required';
    if (!row.amount && row.amount != 0) rowErr.amount = 'Required';
    if (row.amount && Number.isNaN(Number(row.amount))) rowErr.amount = 'Must be a number';
    if (row.amount && Number(row.amount) < 0) rowErr.amount = 'Must be positive';
    const dateError = getRateRowDateError(row);
    if (dateError) rowErr.validRange = dateError;
    if (row.additionalCharge && Number.isNaN(Number(row.additionalCharge))) rowErr.additionalCharge = 'Must be a number';
    return rowErr;
};

const markDuplicateRateRows = (rateGrid, rateErrors) => {
    const seenRowSignatures = new Map();
    let hasDuplicateRows = false;
    rateGrid.forEach((row, i) => {
        if (!row.transactionType || !row.chargeType || !row.size) return;
        const signature = getRateRowSignature(row);
        if (seenRowSignatures.has(signature)) {
            hasDuplicateRows = true;
            const firstIndex = seenRowSignatures.get(signature);
            rateErrors[i] = rateErrors[i] || {};
            rateErrors[i].duplicate = true;
            rateErrors[firstIndex] = rateErrors[firstIndex] || {};
            rateErrors[firstIndex].duplicate = true;
        } else {
            seenRowSignatures.set(signature, i);
        }
    });
    return hasDuplicateRows;
};

const CreateEditPricingTemplate = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams();
    const dispatch = useDispatch();
    const isNew = id === 'new';
    const isViewMode = location.state?.isViewMode || false;

    const { showAccountingMenu } = useSelector((state) => state.accountingReducer);
    const { filterCompanies, filterAllSellers } = useSelector((state) => state.RateCardReducer);
    const { warehouseList } = useSelector((state) => state.InventoryManagement);
    const { SizeTypeList, UnitTypeList, PackingTypeList, TransactionTypeList } = useSelector((state) => state.serviceRateCardReducer);
    const { currentTemplate, currentTemplateStatus } = useSelector((state) => state.customPricingTemplateReducer);

    const [companyList, setCompanyList] = useState([]);
    const [sellerList, setSellerList] = useState([]);
    const [allSellers, setAllSellers] = useState([]);
    const [warehouseOptions, setWarehouseOptions] = useState([]);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [skuOptions, setSkuOptions] = useState([]);
    const [carrierConnectOptions, setCarrierConnectOptions] = useState([]);
    const [serviceTypeOptions, setServiceTypeOptions] = useState([]);
    const [subServiceTypeOptions, setSubServiceTypeOptions] = useState([]);
    const serviceTypesFetchedRef = useRef(false);
    const subServiceCacheByServiceIdRef = useRef(new Map());
    const subServiceInFlightByServiceIdRef = useRef(new Map());
    const skuCacheByRequestKeyRef = useRef(new Map());
    const skuInFlightByRequestKeyRef = useRef(new Map());
    const lastSkuRequestKeyRef = useRef('');
    const preselectedSkuIdsRef = useRef([]);
    const carrierConnectCacheByRequestKeyRef = useRef(new Map());
    const carrierConnectInFlightByRequestKeyRef = useRef(new Map());
    const lastCarrierConnectRequestKeyRef = useRef('');
    const preselectedCarrierConnectIdsRef = useRef([]);
    const preselectedCarrierConnectDetailsRef = useRef([]);
    const populatedTemplateKeyRef = useRef(null);

    const userDetails = JSON.parse(localStorage.getItem('currentUser') || '{}');
    const tenantId = userDetails?.Value?.tenantId || 0;

    const [formData, setFormData] = useState({
        templateName: '',
        warehouses: [],
        companies: [],
        sellers: [],
        serviceTypes: [],
        subServiceTypes: [],
        skus: [],
        carrierConnectIds: [],
        rateGrid: [emptyRateRow()],
    });

    const getServiceRequestPayload = (serviceTypeMasterId) => {
        const payload = {
            tenantId,
            searchKeywords: '',
        };
        if (serviceTypeMasterId) {
            payload.serviceTypeMaster_Id = serviceTypeMasterId;
        }
        return payload;
    };

    const mapServiceOptions = (response) => {
        const list = response?.data?.Entity?.ListServicesEntity || [];
        return list.map((item) => ({ value: item.Id, label: item.name }));
    };

    const mapSkuOptions = (response) => {
        const list = response?.data?.Entity?.ListSku || [];
        return list.map((item) => ({
            value: item.Id,
            label: item.SKU || String(item.Id),
            sku: item.SKU || '',
            itemName: item.ItemName || '',
            companyName: item.CompanyName || '',
            sellerName: item.SellerName || '',
            companySellerId: item.CompanySeller_Id || 0,
            quantity: item.Quantity ?? 0,
            itemType: item.ItemType ?? item.itemType ?? item.ItemType_Id ?? item.itemTypeId ?? null,
        }));
    };

    const mapCarrierConnectOptions = (response) => {
        const list = response?.data?.Entity?.ListCarrierConnectIds || [];
        return list.map((item) => ({
            value: item.Id,
            label: item.CarrierConnectNumber || String(item.Id),
            carrierConnectNumber: item.CarrierConnectNumber || '',
            companySellerId: item.CompanySeller_Id || 0,
        }));
    };

    const fetchServiceTypeOptions = async () => {
        if (serviceTypesFetchedRef.current) return;
        const response = await getAllPricingServiceTypes(getServiceRequestPayload());
        if (response?.data?.Status === 200) {
            setServiceTypeOptions(mapServiceOptions(response));
            serviceTypesFetchedRef.current = true;
        }
    };

    const fetchSubServicesForServiceType = async (serviceTypeMasterId) => {
        if (subServiceCacheByServiceIdRef.current.has(serviceTypeMasterId)) {
            return subServiceCacheByServiceIdRef.current.get(serviceTypeMasterId);
        }

        if (subServiceInFlightByServiceIdRef.current.has(serviceTypeMasterId)) {
            return subServiceInFlightByServiceIdRef.current.get(serviceTypeMasterId);
        }

        const requestPromise = getPricingSubServiceTypesById(getServiceRequestPayload(serviceTypeMasterId))
            .then((response) => {
                const options = mapServiceOptions(response);
                subServiceCacheByServiceIdRef.current.set(serviceTypeMasterId, options);
                return options;
            })
            .finally(() => {
                subServiceInFlightByServiceIdRef.current.delete(serviceTypeMasterId);
            });

        subServiceInFlightByServiceIdRef.current.set(serviceTypeMasterId, requestPromise);
        return requestPromise;
    };

    const fetchSkusBySelection = async ({ serviceTypeId, companyIds, sellerIds, warehouseIds, searchText = '' }) => {
        const requestKey = getRequestCacheKey(serviceTypeId, companyIds, sellerIds, warehouseIds, searchText);
        lastSkuRequestKeyRef.current = requestKey;

        if (skuCacheByRequestKeyRef.current.has(requestKey)) {
            return skuCacheByRequestKeyRef.current.get(requestKey);
        }

        if (skuInFlightByRequestKeyRef.current.has(requestKey)) {
            return skuInFlightByRequestKeyRef.current.get(requestKey);
        }

        const requestPayload = {
            pageIndex: 1,
            pageSize: 1000,
            sortColumn: '',
            sortOrder: 'ASCENDING',
            searchkey: '',
            serviceTypeId,
            companyIdList: companyIds,
            sellerIdList: sellerIds,
            warehouseList: warehouseIds,
            searchText,
        };

        const requestPromise = getPricingSkusById(requestPayload)
            .then((response) => {
                const options = mapSkuOptions(response);
                skuCacheByRequestKeyRef.current.set(requestKey, options);
                return options;
            })
            .finally(() => {
                skuInFlightByRequestKeyRef.current.delete(requestKey);
            });

        skuInFlightByRequestKeyRef.current.set(requestKey, requestPromise);
        return requestPromise;
    };

    const fetchCarrierConnectBySelection = async ({ serviceTypeId, companyIds, sellerIds, searchText = '' }) => {
        const requestKey = getSimpleRequestCacheKey(serviceTypeId, companyIds, sellerIds, searchText);
        lastCarrierConnectRequestKeyRef.current = requestKey;

        if (carrierConnectCacheByRequestKeyRef.current.has(requestKey)) {
            return carrierConnectCacheByRequestKeyRef.current.get(requestKey);
        }

        if (carrierConnectInFlightByRequestKeyRef.current.has(requestKey)) {
            return carrierConnectInFlightByRequestKeyRef.current.get(requestKey);
        }

        const requestPayload = {
            pageIndex: 1,
            pageSize: 1000,
            sortColumn: '',
            sortOrder: 'ASCENDING',
            searchkey: '',
            serviceTypeId,
            companyIdList: companyIds,
            sellerIdList: sellerIds,
            searchText,
        };

        const requestPromise = getPricingCarrierConnectIds(requestPayload)
            .then((response) => {
                const options = mapCarrierConnectOptions(response);
                carrierConnectCacheByRequestKeyRef.current.set(requestKey, options);
                return options;
            })
            .finally(() => {
                carrierConnectInFlightByRequestKeyRef.current.delete(requestKey);
            });

        carrierConnectInFlightByRequestKeyRef.current.set(requestKey, requestPromise);
        return requestPromise;
    };

    const syncSelectedSkus = (options, selectedSkuIds = []) => {
        const skuIdSet = new Set(normalizeIdList(selectedSkuIds));
        const selectedSkus = skuIdSet.size ? options.filter((item) => skuIdSet.has(item.value)) : [];
        setFormData((prev) => ({ ...prev, skus: selectedSkus }));
    };

    const syncSelectedCarrierConnectIds = (options, selectedIds = []) => {
        const idSet = new Set(normalizeIdList(selectedIds));
        const selectedCarrierConnectIds = idSet.size ? options.filter((item) => idSet.has(item.value)) : [];
        setFormData((prev) => ({ ...prev, carrierConnectIds: selectedCarrierConnectIds }));
    };

    const fetchWarehousesByCompanySeller = async (companyIds = [], sellerIds = []) => {
        const requestPairs = buildWarehouseRequestPairs(companyIds, sellerIds);
        if (!requestPairs.length) return [];

        const responses = await Promise.all(
            requestPairs.map(({ companyId, sellerId }) => (
                getSellerWarehouseService({
                    companyId,
                    sellerId,
                    tenantId,
                }).catch(() => null)
            ))
        );

        return _.uniqBy(
            responses.flatMap((response) => mapSellerWarehouseEntityToOptions(response?.data?.EntityList || [])),
            'value'
        );
    };

    const reconcileSelectedWarehouses = (options = []) => {
        setFormData((prev) => {
            const selectedWarehouseIds = new Set(
                normalizeIdList((prev.warehouses || []).map((item) => item?.value || item?.id))
            );
            if (!selectedWarehouseIds.size) return prev;

            const mergedOptions = _.uniqBy([...(options || []), ...(prev.warehouses || [])], 'value');
            const selectedWarehouses = mergedOptions.filter((item) => selectedWarehouseIds.has(item.value));

            return { ...prev, warehouses: selectedWarehouses };
        });
    };

    const fetchSubServiceTypeOptions = async (selectedServices, selectedSubServiceIds = []) => {
        const selectedServiceIds = _.map(selectedServices || [], 'value');
        if (!selectedServiceIds.length) {
            setSubServiceTypeOptions([]);
            setFormData((prev) => ({ ...prev, subServiceTypes: [] }));
            return [];
        }

        const responses = await Promise.all(
            selectedServiceIds.map((serviceTypeMasterId) => fetchSubServicesForServiceType(serviceTypeMasterId))
        );
        const mergedSubServices = _.uniqBy(
            responses.flatMap((items) => items || []),
            'value'
        );
        setSubServiceTypeOptions(mergedSubServices);

        const selectedSubServiceSet = new Set(selectedSubServiceIds || []);
        const selectedOptions = selectedSubServiceSet.size
            ? mergedSubServices.filter((item) => selectedSubServiceSet.has(item.value))
            : [];
        const mergedSubServiceIdSet = new Set(_.map(mergedSubServices, 'value'));

        setFormData((prev) => ({
            ...prev,
            subServiceTypes: selectedSubServiceSet.size
                ? selectedOptions
                : filterOptionsByValueSet(prev.subServiceTypes || [], mergedSubServiceIdSet),
        }));

        return mergedSubServices;
    };

    const populateFormFromTemplate = async (data) => {
        const warehouseIds = data.warehouseIds || _.map(data.warehouses || [], 'value');
        const companyIds = data.companyIds || _.map(data.companies || [], item => item.Id || item.value);
        const sellerIds = data.sellerIds || _.map(data.sellers || [], item => item.id || item.value);

        const warehouseOptionsFromSelection = await fetchWarehousesByCompanySeller(companyIds, sellerIds);
        const effectiveWarehouseOptions = warehouseOptionsFromSelection.length ? warehouseOptionsFromSelection : (warehouseList || []);
        const selectedWarehouses = getSelectedOptionsByIds(effectiveWarehouseOptions, warehouseIds, (item) => item.value);
        const selectedCompanies = getSelectedOptionsByIds(companyList, companyIds, (item) => item.Id || item.value);
        const selectedSellers = getSelectedOptionsByIds((sellerList || allSellers), sellerIds, (item) => item.id || item.value);

        const serviceTypeValues = getServiceTypeValuesFromTemplate(data);
        const serviceTypeSet = new Set(serviceTypeValues);
        const selectedServiceTypes = serviceTypeOptions.filter((item) => serviceTypeSet.has(item.value));

        const subServiceValues = data.subServiceTypeIds?.length
            ? data.subServiceTypeIds
            : _.map(data.subServiceTypes || [], (item) => (typeof item === 'object' ? item.value : item));
        const selectedSkuIds = data.skuProductIds?.length
            ? data.skuProductIds
            : _.map(data.skus || [], (item) => item.value || item.id || item);
        const selectedCarrierConnectIds = data.carrierConnectIdList?.length
            ? data.carrierConnectIdList
            : _.map(data.carrierConnectIds || [], (item) => item.value || item.id || item);
        const selectedCarrierConnectDetails = mapCarrierConnectDetailsToOptions(data.carrierConnectDetails || []);

        const mappedRateGrid = data.rateGrid?.length
            ? data.rateGrid.map((r) => ({ ...r, _uid: r._uid || genUid() }))
            : [emptyRateRow()];

        setWarehouseOptions(effectiveWarehouseOptions);

        setFormData({
            templateName: data.templateName || '',
            warehouses: selectedWarehouses,
            companies: selectedCompanies,
            sellers: selectedSellers,
            serviceTypes: selectedServiceTypes,
            subServiceTypes: [],
            skus: [],
            carrierConnectIds: selectedCarrierConnectDetails,
            rateGrid: mappedRateGrid,
        });

        fetchSubServiceTypeOptions(selectedServiceTypes, subServiceValues);
        preselectedSkuIdsRef.current = normalizeIdList(selectedSkuIds);
        preselectedCarrierConnectIdsRef.current = normalizeIdList(selectedCarrierConnectIds);
        preselectedCarrierConnectDetailsRef.current = selectedCarrierConnectDetails;
    };

    // Fetch dropdown data on mount
    useEffect(() => {
        dispatch(fetchCompanyFilters({
            pageIndex: 1, pageSize: 10, sortColumn: 'name', sortOrder: 'ascending'
        }));
        dispatch(getFilterSellers({
            pageIndex: 1, pageSize: 10, sortColumn: 'name', sortOrder: 'ASCENDING',
            companyIdList: [0], screenType: 2,
        }));
        dispatch(getWarehouseListing({ pageIndex: 1, pageSize: 1000 }));
        dispatch(getSizeTypeList());
        dispatch(getChargeUnitTypeList());
        dispatch(getPackingTypeList());
        dispatch(getTransactionTypeList());
        fetchServiceTypeOptions();
    }, []);

    // Load template data for edit mode
    useEffect(() => {
        if (!isNew && id) {
            dispatch(fetchCustomPricingTemplateById(id));
        }
    }, [dispatch, id, isNew, isViewMode]);

    // Populate form when editing (once per template load — must not re-run just
    // because sellerList/companyList change later as a result of the user's own
    // edits, or it would keep resetting their in-progress selections)
    useEffect(() => {
        if (isNew) return;
        const populatedTemplateKey = `${id}-${isViewMode ? 'view' : 'edit'}`;
        if (populatedTemplateKeyRef.current === populatedTemplateKey) return;

        const data = currentTemplateStatus === 'SUCCESS' ? currentTemplate : null;
        if (!data) return;
        if (!companyList.length || !serviceTypeOptions.length || !warehouseList.length || !allSellers.length) return;

        let isActive = true;
        const populateTemplate = async () => {
            await populateFormFromTemplate(data);
            if (isActive) {
                populatedTemplateKeyRef.current = populatedTemplateKey;
            }
        };

        populateTemplate();

        return () => {
            isActive = false;
        };
    }, [
        currentTemplate,
        currentTemplateStatus,
        isNew,
        isViewMode,
        id,
        warehouseList,
        companyList,
        sellerList,
        allSellers,
        serviceTypeOptions,
    ]);

    useEffect(() => {
        const companyIds = normalizeIdList((formData.companies || []).map((item) => item.Id || item.value));
        const sellerIds = normalizeIdList((formData.sellers || []).map((item) => item.id || item.value));

        if (!companyIds.length || !sellerIds.length) {
            setWarehouseOptions(warehouseList || []);
            return;
        }

        let isActive = true;
        fetchWarehousesByCompanySeller(companyIds, sellerIds).then((options) => {
            if (!isActive) return;

            const nextWarehouseOptions = options.length ? options : (warehouseList || []);
            setWarehouseOptions(nextWarehouseOptions);
            reconcileSelectedWarehouses(nextWarehouseOptions);
        });

        return () => {
            isActive = false;
        };
    }, [formData.companies, formData.sellers, warehouseList]);

    // Set company & seller lists from Redux
    useEffect(() => {
        if (filterCompanies?.Entity?.listCompanyMasterOrdersEntity?.length) {
            setCompanyList(
                getSelectList(filterCompanies.Entity.listCompanyMasterOrdersEntity, 'Id', 'name')
            );
        }
        const sellers = filterAllSellers?.EntityList || [];
        if (sellers.length) {
            const formatted = getSelectList(sellers, 'id', 'sellerName');
            setAllSellers(formatted);
            setSellerList(formatted);
        }
    }, [filterCompanies, filterAllSellers]);

    const fetchSellers = async (companyIds) => {
        if (!companyIds?.length) {
            setSellerList(allSellers);
            setFormData((prev) => (prev.sellers?.length ? { ...prev, sellers: [] } : prev));
            return;
        }
        const res = await getAllSellersList({ companyIds });
        if (res?.data?.Status === 200) {
            const dataList = res.data.Entity.listSellerMasterOrdersEntity.map((item) => ({
                ...item,
                label: item.sellerAndCompany,
                value: item.id,
            }));
            setSellerList(dataList);

            // The seller-picker's currently selected sellers may have come from the
            // broad "all sellers" list (edit-mode load), which doesn't reliably carry
            // a companyIds field — so validity is checked by id against this
            // company-scoped fetch (which does), not by parsing companyIds ourselves.
            const validSellerIds = new Set(dataList.map((s) => s.id ?? s.value));
            setFormData((prev) => {
                const stillValidSellers = (prev.sellers || []).filter((seller) => validSellerIds.has(seller.id ?? seller.value));
                const merged = computeAutoSelectedSellers(dataList, companyIds, stillValidSellers);
                const isSameSelection = merged.length === (prev.sellers?.length || 0)
                    && merged.every((seller, index) => (seller.id ?? seller.value) === (prev.sellers[index]?.id ?? prev.sellers[index]?.value));
                return isSameSelection ? prev : { ...prev, sellers: merged };
            });
        }
    };

    // Cascading sub-service types
    const isCarrierConnectSelected = formData.serviceTypes?.some((st) => Number.parseInt(st.value, 10) === 5);

    const getCarrierConnectPlaceholder = () => {
        if (!formData.companies?.length || !formData.sellers?.length) return "Select Company & Seller first";
        if (!isCarrierConnectSelected) return "Select 'Carrier Connect' service type first";
        return "Select Carrier Connect ID(s)";
    };

    // Check if any SKU is a bundle/kit type
    const hasBundleKit = formData.skus?.some(sku => sku.itemType === 1 || sku.itemType === 'Bundle');

    // Handlers
    const handleInputChange = (field, value) => {
        setFormData({ ...formData, [field]: value });
        if (errors[field]) {
            setErrors({ ...errors, [field]: '' });
        }
    };

    const handleCompanyChange = (selected) => {
        const selectedCompanies = selected || [];
        // Sellers are reconciled inside fetchSellers once the company-scoped fetch
        // resolves (that response reliably tells us which sellers still apply) —
        // don't drop or reset the seller selection here.
        // Preserve the SKU / carrier-connect selections the user already made so
        // that adding another company doesn't wipe existing data — the effects
        // below re-fetch the options and re-apply whichever selections are still
        // valid for the new company set.
        preselectedSkuIdsRef.current = normalizeIdList(_.map(formData.skus || [], 'value'));
        preselectedCarrierConnectIdsRef.current = normalizeIdList(_.map(formData.carrierConnectIds || [], 'value'));
        setFormData({ ...formData, companies: selectedCompanies });
        setSkuOptions([]);
        setCarrierConnectOptions([]);
        if (errors.companies) {
            setErrors({ ...errors, companies: '' });
        }
        fetchSellers(_.map(selectedCompanies, 'Id'));
    };

    const handleSellerChange = (selected) => {
        // Preserve the SKU / carrier-connect selections already made so adding
        // another seller doesn't wipe existing data — the effects below re-fetch
        // the options and re-apply whichever selections are still valid.
        preselectedSkuIdsRef.current = normalizeIdList(_.map(formData.skus || [], 'value'));
        preselectedCarrierConnectIdsRef.current = normalizeIdList(_.map(formData.carrierConnectIds || [], 'value'));
        setFormData({ ...formData, sellers: selected || [] });
        setSkuOptions([]);
        setCarrierConnectOptions([]);
        if (errors.sellers) {
            setErrors({ ...errors, sellers: '' });
        }
    };

    const handleServiceTypeChange = (selected) => {
        const selectedServices = selected || [];
        fetchSubServiceTypeOptions(selectedServices);
        setSkuOptions([]);
        setCarrierConnectOptions([]);
        preselectedSkuIdsRef.current = [];
        preselectedCarrierConnectIdsRef.current = [];
        setFormData({
            ...formData,
            serviceTypes: selectedServices,
            skus: [],
            carrierConnectIds: [],
        });
        if (errors.serviceTypes) {
            setErrors({ ...errors, serviceTypes: '' });
        }
    };

    useEffect(() => {
        const companyIds = normalizeIdList((formData.companies || []).map((item) => item.Id || item.value));
        const sellerIds = normalizeIdList((formData.sellers || []).map((item) => item.id || item.value));
        const warehouseIds = normalizeIdList(_.map(formData.warehouses || [], 'value'));
        const serviceTypeIds = normalizeIdList(_.map(formData.serviceTypes || [], 'value'));
        const primaryServiceTypeId = serviceTypeIds.length ? serviceTypeIds[0] : 0;

        const hasSelectionForSkuLookup = Boolean(primaryServiceTypeId && companyIds.length && sellerIds.length);
        if (!hasSelectionForSkuLookup) {
            setSkuOptions([]);
            setFormData((prev) => ({ ...prev, skus: [] }));
            return;
        }

        const requestKey = getRequestCacheKey(primaryServiceTypeId, companyIds, sellerIds, warehouseIds, '');
        fetchSkusBySelection({
            serviceTypeId: primaryServiceTypeId,
            companyIds,
            sellerIds,
            warehouseIds,
            searchText: '',
        }).then((options) => {
            if (lastSkuRequestKeyRef.current !== requestKey) {
                return;
            }

            setSkuOptions(options);

            if (preselectedSkuIdsRef.current.length) {
                syncSelectedSkus(options, preselectedSkuIdsRef.current);
                preselectedSkuIdsRef.current = [];
            }
        });
    }, [
        formData.companies,
        formData.sellers,
        formData.serviceTypes,
        formData.warehouses,
    ]);

    useEffect(() => {
        const companyIds = normalizeIdList((formData.companies || []).map((item) => item.Id || item.value));
        const sellerIds = normalizeIdList((formData.sellers || []).map((item) => item.id || item.value));
        const serviceTypeIds = normalizeIdList(_.map(formData.serviceTypes || [], 'value'));

        const hasCarrierConnectServiceType = serviceTypeIds.includes(5);
        const hasSelectionForCarrierLookup = Boolean(hasCarrierConnectServiceType && companyIds.length && sellerIds.length);
        if (!hasSelectionForCarrierLookup) {
            setCarrierConnectOptions([]);
            setFormData((prev) => ({ ...prev, carrierConnectIds: [] }));
            return;
        }

        const requestKey = getSimpleRequestCacheKey(5, companyIds, sellerIds, '');
        fetchCarrierConnectBySelection({
            serviceTypeId: 5,
            companyIds,
            sellerIds,
            searchText: '',
        }).then((options) => {
            if (lastCarrierConnectRequestKeyRef.current !== requestKey) {
                return;
            }

            const preselectedDetails = preselectedCarrierConnectDetailsRef.current || [];
            const mergedCarrierConnectOptions = _.uniqBy([
                ...(options || []),
                ...preselectedDetails,
            ], 'value');

            setCarrierConnectOptions(mergedCarrierConnectOptions);
            if (preselectedCarrierConnectIdsRef.current.length) {
                syncSelectedCarrierConnectIds(mergedCarrierConnectOptions, preselectedCarrierConnectIdsRef.current);
                preselectedCarrierConnectIdsRef.current = [];
            }
        });
    }, [
        formData.companies,
        formData.sellers,
        formData.serviceTypes,
    ]);

    // Rate grid handlers
    const clearRateGridFieldError = (index, field) => {
        if (!errors.rateGrid?.[index]?.[field]) return;
        const updatedRateGrid = [...errors.rateGrid];
        const restRowErr = { ...updatedRateGrid[index] };
        delete restRowErr[field];
        updatedRateGrid[index] = restRowErr;
        setErrors({ ...errors, rateGrid: updatedRateGrid });
    };

    const clearDuplicateRateErrors = () => {
        setErrors((prev) => {
            if (!prev.rateGridDuplicate && !prev.rateGrid?.some((row) => row?.duplicate)) {
                return prev;
            }
            const updated = { ...prev };
            delete updated.rateGridDuplicate;
            updated.rateGrid = prev.rateGrid?.map((row) => {
                if (!row?.duplicate) return row;
                const restRow = { ...row };
                delete restRow.duplicate;
                return restRow;
            });
            return updated;
        });
    };

    const handleRateChange = (index, field, value) => {
        const updated = structuredClone(formData.rateGrid);
        updated[index][field] = value;
        setFormData({ ...formData, rateGrid: updated });
        clearRateGridFieldError(index, field);
        if (['transactionType', 'chargeType', 'size', 'chargeUnit'].includes(field)) {
            clearDuplicateRateErrors();
        }
    };

    const handleRateDateChange = (date, fieldName, index) => {
        const updated = structuredClone(formData.rateGrid);
        updated[index][fieldName] = date;
        setFormData({ ...formData, rateGrid: updated });
        clearRateGridFieldError(index, 'validRange');
    };

    const addRateRow = () => {
        setFormData({
            ...formData,
            rateGrid: [...formData.rateGrid, emptyRateRow()],
        });
    };

    const removeRateRow = (index) => {
        if (formData.rateGrid.length <= 1) return;
        const updated = formData.rateGrid.filter((_, i) => i !== index);
        setFormData({ ...formData, rateGrid: updated });
    };

    // Validation
    const validate = () => {
        const newErrors = {};
        if (!formData.templateName?.trim()) newErrors.templateName = 'Template Name is required';
        if (!formData.companies?.length) newErrors.companies = 'At least one Company is required';
        if (!formData.sellers?.length) newErrors.sellers = 'At least one Seller is required';
        if (!formData.serviceTypes?.length) newErrors.serviceTypes = 'At least one Service Type is required';

        // Rate grid validation
        const rateErrors = [];
        formData.rateGrid.forEach((row, i) => {
            const rowErr = validateRateRow(row);
            if (Object.keys(rowErr).length) rateErrors[i] = rowErr;
        });

        // Duplicate rate row validation: (Transaction Type, Charge Type, Size, Charge/Unit) must be unique
        if (markDuplicateRateRows(formData.rateGrid, rateErrors)) {
            newErrors.rateGridDuplicate = 'Each rate row must have a unique combination of Transaction Type, Charge Type, Size and Charge/Unit. Please change at least one value in the highlighted rows.';
        }

        if (rateErrors.length || rateErrors.some(Boolean)) {
            newErrors.rateGrid = rateErrors;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validate()) {
            toast.error('Please fix the validation errors before saving');
            return;
        }
        setSaving(true);

        const nowIso = moment().format('YYYY-MM-DDTHH:mm:ss');

        const payload = {
            pageIndex: 1,
            pageSize: 10,
            sortColumn: '',
            sortOrder: 'ASCENDING',
            searchkey: '',
            employeeId: userDetails?.Value?.employeeId || 0,
            companyId: 0,
            sellerId: 0,
            createdBy: userDetails?.Value?.employeeId || 0,
            createdDate: nowIso,
            isActive: true,
            modifiedBy: userDetails?.Value?.employeeId || 0,
            modifiedDate: nowIso,
            companySellerId: userDetails?.Value?.companySellerId || 0,
            role: userDetails?.Value?.roleId || 0,
            isSuperAdmin: !!userDetails?.Value?.isSuperAdmin,
            tenantId,
            isBillingCycle: false,
            userEmail: userDetails?.Value?.userEmailId || userDetails?.Value?.email || '',
            customPricingTemplateId: isNew ? 0 : Number.parseInt(id, 10),
            templateName: formData.templateName.trim(),
            serviceTypeMasterId: formData.serviceTypes?.[0]?.value || 0,
            subServiceTypeIds: _.map(formData.subServiceTypes, 'value'),
            skuIds: formData.skus.map((sku) => `${sku.sku}@${sku.value}@${sku.companySellerId || 0}`),
            companyIdList: formData.companies.map((item) => item.Id || item.value),
            sellerIdList: formData.sellers.map((item) => item.id || item.value),
            warehouseList: _.map(formData.warehouses, 'value'),
            carrierConnectIdList: normalizeIdList(_.map(formData.carrierConnectIds, 'value')),
            customPricingRates: formData.rateGrid.map(row => ({
                transactionTypeId: row.transactionType?.value || row.transactionType || 0,
                chargeTypeId: row.chargeType?.value || row.chargeType || 0,
                sizeTypeId: row.size?.value || row.size || 0,
                unitId: row.chargeUnit?.value || row.chargeUnit || 0,
                validFrom: row.validFrom,
                validTo: row.validTo,
                amount: Number.parseFloat(row.amount) || 0,
                additionalCharge: Number.parseFloat(row.additionalCharge) || 0,
                isActive: true,
            })),
        };

        const response = await Promise.resolve(dispatch(createUpdateCustomPricingTemplate(payload)));
        if (response) {
            toast.success(isNew ? 'Template created successfully' : 'Template updated successfully');
            navigate('/accounting/pricing-templates');
        }
        setSaving(false);
    };

    const handleBack = () => {
        navigate('/accounting/pricing-templates');
    };

    const handleEdit = () => {
        navigate(`/accounting/pricing-templates/template-${id}`, { replace: true, state: { isViewMode: false } });
    };

    const preventNonNumeric = (evt) => {
        if (["e", "E", "+", "-"].includes(evt.key)) evt.preventDefault();
    };

    const formatDecimal = (value) => {
        if (!value && value !== 0) return '';
        const num = Number.parseFloat(value);
        return Number.isNaN(num) ? value : num.toFixed(2);
    };

    const getPageTitle = () => {
        if (isNew) return 'Create New Template';
        if (isViewMode) return 'View Template';
        return 'Edit Template';
    };

    if (!isNew && currentTemplateStatus === 'PENDING') {
        return <LoaderSpinner />;
    }

    return (
        <div className={`${showAccountingMenu ? 'col-10' : 'col-12'}`}>
            <div className="main__tile_heading cus-btn-include-sec">
                <span>{getPageTitle()}</span>
                <div className="top_right_button_container">
                    {!isViewMode && (
                        <button
                            className="btn cus-seconday-bg-btn top-btn"
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? 'Saving...' : 'Save Template'}
                        </button>
                    )}
                    {isViewMode && (
                        <button
                            className="btn cus-seconday-bg-btn top-btn"
                            onClick={handleEdit}
                        >
                            Edit
                        </button>
                    )}
                    <button
                        className="btn cus-primary-transparent-btn top-btn"
                        onClick={handleBack}
                    >
                        Back
                    </button>
                </div>
            </div>

            {/* Form Fields */}
            <div className="cus-page-content-sec">
                <div className="row mb-3">
                    {/* Left Column */}
                    <div className="col-lg-6 col-sm-12">
                        <div className="row mb-3">
                            <div className="col-12">
                                <label htmlFor="templateName" className="labelHead">Enter Template Name:<span className="error-icon">*</span></label>
                                <input
                                    id="templateName"
                                    type="text"
                                    className="form-control"
                                    value={formData.templateName}
                                    onChange={(e) => handleInputChange('templateName', e.target.value)}
                                    disabled={isViewMode}
                                    placeholder="Enter template name"
                                    maxLength={200}
                                />
                                <div className="require-field">{errors.templateName}</div>
                            </div>
                        </div>
                        <div className="row mb-3">
                            <div className="col-12">
                                <label htmlFor="warehouses" className="labelHead">Select Warehouse(s):</label>
                                <Select
                                    isMulti
                                    closeMenuOnSelect={false}
                                    options={warehouseOptions || []}
                                    value={formData.warehouses}
                                    onChange={(val) => handleInputChange('warehouses', val || [])}
                                    isDisabled={isViewMode}
                                    className="activeSellerListInput"
                                    classNamePrefix="list"
                                    placeholder="Select Warehouse(s)"
                                />
                            </div>
                        </div>
                        <div className="row mb-3">
                            <div className="col-12">
                                <label htmlFor="companies" className="labelHead">Select Company(s):<span className="error-icon">*</span></label>
                                <Select
                                    isMulti
                                    closeMenuOnSelect={false}
                                    options={companyList}
                                    value={formData.companies}
                                    onChange={handleCompanyChange}
                                    isDisabled={isViewMode}
                                    className="activeSellerListInput"
                                    classNamePrefix="list"
                                    placeholder="Select Company(s)"
                                />
                                <div className="require-field">{errors.companies}</div>
                            </div>
                        </div>
                        <div className="row mb-3">
                            <div className="col-12">
                                <label htmlFor="sellers" className="labelHead">Select Seller(s):<span className="error-icon">*</span></label>
                                <Select
                                    isMulti
                                    options={sellerList}
                                    value={formData.sellers}
                                    onChange={(val) => handleSellerChange(val)}
                                    isDisabled={isViewMode}
                                    className="activeSellerListInput"
                                    classNamePrefix="listSeller"
                                    placeholder="Select Seller(s)"
                                />
                                <div className="require-field">{errors.sellers}</div>
                            </div>
                        </div>
                    </div>

                    {/* Right Column */}
                    <div className="col-lg-6 col-sm-12">
                        <div className="row mb-3">
                            <div className="col-12">
                                <label htmlFor="serviceTypes" className="labelHead">Select Service Type:<span className="error-icon">*</span></label>
                                <Select
                                    options={serviceTypeOptions}
                                    value={formData.serviceTypes?.[0] || null}
                                    onChange={(val) => handleServiceTypeChange(val ? [val] : [])}
                                    isDisabled={isViewMode}
                                    className="activeSellerListInput"
                                    classNamePrefix="list"
                                    placeholder="Select Service Type"
                                />
                                <div className="require-field">{errors.serviceTypes}</div>
                            </div>
                        </div>
                        <div className="row mb-3">
                            <div className="col-12">
                                <label htmlFor="subServiceTypes" className="labelHead">Select Sub-Service Type (Optional):</label>
                                <Select
                                    isMulti
                                    closeMenuOnSelect={false}
                                    options={subServiceTypeOptions}
                                    value={formData.subServiceTypes}
                                    onChange={(val) => handleInputChange('subServiceTypes', val || [])}
                                    isDisabled={isViewMode || !formData.serviceTypes?.length}
                                    className="activeSellerListInput"
                                    classNamePrefix="list"
                                    placeholder="Select Sub-Service Type(s)"
                                    noOptionsMessage={() => formData.serviceTypes?.length ? 'No sub-services' : 'Select a Service Type first'}
                                />
                            </div>
                        </div>
                        <div className="row mb-3">
                            <div className="col-12">
                                <label htmlFor="skus" className="labelHead">Select Individual/Bundle/Kit/Return SKU (Optional):</label>
                                <Select
                                    isMulti
                                    closeMenuOnSelect={false}
                                    options={skuOptions}
                                    value={formData.skus}
                                    onChange={(val) => handleInputChange('skus', val || [])}
                                    isDisabled={isViewMode || !formData.companies?.length || !formData.sellers?.length || !formData.serviceTypes?.length}
                                    className="activeSellerListInput"
                                    classNamePrefix="list"
                                    placeholder={formData.companies?.length && formData.sellers?.length && formData.serviceTypes?.length ? "Select SKU(s)" : "Select Company, Seller, and Service Type first"}
                                    noOptionsMessage={() => 'No SKU records found'}
                                    formatOptionLabel={formatSkuOptionLabel}
                                />
                            </div>
                        </div>
                        <div className="row mb-3">
                            <div className="col-12">
                                <label htmlFor="carrierConnectIds" className="labelHead">Select Carrier Connect ID(s) (Optional):</label>
                                <Select
                                    isMulti
                                    closeMenuOnSelect={false}
                                    options={carrierConnectOptions}
                                    value={formData.carrierConnectIds}
                                    onChange={(val) => handleInputChange('carrierConnectIds', val || [])}
                                    isDisabled={isViewMode || !isCarrierConnectSelected || !formData.companies?.length || !formData.sellers?.length}
                                    className="activeSellerListInput"
                                    classNamePrefix="list"
                                    placeholder={getCarrierConnectPlaceholder()}
                                    noOptionsMessage={() => 'No carrier connect records found'}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Kit/Bundle Pricing Indicator */}
                {hasBundleKit && (
                    <div className="row mb-3">
                        <div className="col-12">
                            <div className="alert alert-info d-flex align-items-center" role="alert">
                                <i className="fa fa-info-circle mr-2" aria-hidden="true"></i>
                                <span>Kit pricing applied — individual component prices overridden</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Rate Grid */}
                <div className="row mb-3">
                    <div className="col-12">
                        <h6 className="mb-3"><b>Select Rates for Charge Type/Size/Charge Unit:</b></h6>
                        <div className="table-responsive">
                            <table className="table table-bordered table-sm" id="rate-grid-table" style={{ fontSize: '13px' }}>
                                <thead>
                                    <tr>
                                        <th>Transaction Type</th>
                                        <th>Charge Type</th>
                                        <th>Size</th>
                                        <th>Charge/Unit</th>
                                        <th>Valid From and Valid To</th>
                                        <th>Amount (Charge/Unit)</th>
                                        <th>Additional Charge</th>
                                        {!isViewMode && <th>Action</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {formData.rateGrid.map((row, index) => (
                                        <tr key={row._uid || index} className={errors.rateGrid?.[index]?.duplicate ? 'duplicate-rate-row' : ''}>
                                            <td style={{ minWidth: '150px' }}>
                                                <Select
                                                    options={TransactionTypeList || []}
                                                    value={typeof row.transactionType === 'object' ? row.transactionType : TransactionTypeList?.find(s => s.value === row.transactionType) || null}
                                                    onChange={(val) => handleRateChange(index, 'transactionType', val)}
                                                    isDisabled={isViewMode}
                                                    className="activeSellerListInput"
                                                    classNamePrefix="list"
                                                    placeholder="Select"
                                                    menuPortalTarget={document.body}
                                                    styles={tableSelectStyles}
                                                />
                                                <div className="require-field">{errors.rateGrid?.[index]?.transactionType}</div>
                                            </td>
                                            <td style={{ minWidth: '150px' }}>
                                                <Select
                                                    options={PackingTypeList || []}
                                                    value={typeof row.chargeType === 'object' ? row.chargeType : PackingTypeList?.find(s => s.value === row.chargeType) || null}
                                                    onChange={(val) => handleRateChange(index, 'chargeType', val)}
                                                    isDisabled={isViewMode}
                                                    className="activeSellerListInput"
                                                    classNamePrefix="list"
                                                    placeholder="Select"
                                                    menuPortalTarget={document.body}
                                                    styles={tableSelectStyles}
                                                />
                                                <div className="require-field">{errors.rateGrid?.[index]?.chargeType}</div>
                                            </td>
                                            <td style={{ minWidth: '130px' }}>
                                                <Select
                                                    options={SizeTypeList || []}
                                                    value={typeof row.size === 'object' ? row.size : SizeTypeList?.find(s => s.value === row.size) || null}
                                                    onChange={(val) => handleRateChange(index, 'size', val)}
                                                    isDisabled={isViewMode}
                                                    className="activeSellerListInput"
                                                    classNamePrefix="list"
                                                    placeholder="Select"
                                                    menuPortalTarget={document.body}
                                                    styles={tableSelectStyles}
                                                />
                                                <div className="require-field">{errors.rateGrid?.[index]?.size}</div>
                                            </td>
                                            <td style={{ minWidth: '130px' }}>
                                                <Select
                                                    options={UnitTypeList || []}
                                                    value={typeof row.chargeUnit === 'object' ? row.chargeUnit : UnitTypeList?.find(u => u.value === row.chargeUnit) || null}
                                                    onChange={(val) => handleRateChange(index, 'chargeUnit', val)}
                                                    isDisabled={isViewMode}
                                                    className="activeSellerListInput"
                                                    classNamePrefix="list"
                                                    placeholder="Select"
                                                    menuPortalTarget={document.body}
                                                    styles={tableSelectStyles}
                                                />
                                                <div className="require-field">{errors.rateGrid?.[index]?.chargeUnit}</div>
                                            </td>
                                            <td style={{ minWidth: '260px' }}>
                                                <div className="d-flex align-items-center">
                                                    <DatePickerComponent
                                                        dateField={row.validFrom}
                                                        handleDateChange={(date) => handleRateDateChange(date, 'validFrom', index)}
                                                        fieldName="validFrom"
                                                        dateFormat="MM-dd-yyyy, h:mm aa"
                                                        timeSelect={true}
                                                        showFutureDate={false}
                                                        maxDate={row.validTo ? new Date(Math.min(new Date(row.validTo).getTime(), Date.now())) : new Date()}
                                                        disabled={isViewMode}
                                                    />
                                                    <span className="mx-2">to</span>
                                                    <DatePickerComponent
                                                        dateField={row.validTo}
                                                        handleDateChange={(date) => handleRateDateChange(date, 'validTo', index)}
                                                        fieldName="validTo"
                                                        dateFormat="MM-dd-yyyy, h:mm aa"
                                                        timeSelect={true}
                                                        minDate={row.validFrom ? new Date(row.validFrom) : null}
                                                        disabled={isViewMode}
                                                    />
                                                </div>
                                                <div className="require-field">{errors.rateGrid?.[index]?.validRange}</div>
                                            </td>
                                            <td style={{ minWidth: '120px' }}>
                                                <div className="d-flex align-items-center">
                                                    <span className="mr-1">$</span>
                                                    <input
                                                        type="number"
                                                        className="form-control form-control-sm"
                                                        value={row.amount}
                                                        onChange={(e) => handleRateChange(index, 'amount', e.target.value)}
                                                        onKeyDown={preventNonNumeric}
                                                        onBlur={(e) => handleRateChange(index, 'amount', formatDecimal(e.target.value))}
                                                        disabled={isViewMode}
                                                        step="0.01"
                                                        min="0"
                                                    />
                                                </div>
                                                <div className="require-field">{errors.rateGrid?.[index]?.amount}</div>
                                            </td>
                                            <td style={{ minWidth: '120px' }}>
                                                <div className="d-flex align-items-center">
                                                    <span className="mr-1">$</span>
                                                    <input
                                                        type="number"
                                                        className="form-control form-control-sm"
                                                        value={row.additionalCharge}
                                                        onChange={(e) => handleRateChange(index, 'additionalCharge', e.target.value)}
                                                        onKeyDown={preventNonNumeric}
                                                        onBlur={(e) => handleRateChange(index, 'additionalCharge', formatDecimal(e.target.value))}
                                                        disabled={isViewMode}
                                                        step="0.01"
                                                        min="0"
                                                    />
                                                </div>
                                            </td>
                                            {!isViewMode && (
                                                <td>
                                                    <button
                                                        className="iconBtnListing cursorPointer"
                                                        onClick={() => removeRateRow(index)}
                                                        disabled={formData.rateGrid.length <= 1}
                                                    >
                                                        <i className="fa fa-trash error-icon" aria-hidden="true"></i>
                                                    </button>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {errors.rateGridDuplicate && (
                            <div className="require-field mt-2">{errors.rateGridDuplicate}</div>
                        )}
                        {!isViewMode && (
                            <button
                                className="btn cus-seconday-bg-btn mt-2"
                                onClick={addRateRow}
                            >
                                <i className="fa fa-plus mr-1" aria-hidden="true"></i> Add Row
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreateEditPricingTemplate;
